import { IconButton, Stack, styled, Typography } from '@mui/material';
import { FC, ReactNode, useContext } from 'react';

import { DropdownIcon } from '../../assets/icons';
import { ListAccordionContext } from './ListAccordion';

export type ListAccordionHeaderVariant = 'default' | 'attention';

export type ListAccordionHeaderProps = {
  title: string;
  variant?: ListAccordionHeaderVariant;
  counter?: ReactNode;
};

const COLOR_VAR = '--list-accordion-color';
const CHEVRON_SELECTOR = '& .expandIcon path';

// MUI v6 migration: keep `variants[]`, expose the component via the
// `slots`/`slotProps` pattern, and read colors from CSS vars
// (`var(--mui-palette-common-*)`) instead of `theme.palette`.
const HeaderRoot = styled(Stack, {
  shouldForwardProp: (prop) => prop !== 'variant' && prop !== 'expanded',
})<{ variant: ListAccordionHeaderVariant; expanded: boolean }>(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  cursor: 'pointer',
  padding: theme.spacing(0, 2),
  variants: [
    {
      props: { expanded: true },
      style: { borderBottom: `1px solid ${theme.palette.common.light2}` },
    },
    {
      props: { variant: 'default' },
      style: {
        [COLOR_VAR]: theme.palette.common.grey2,
        backgroundColor: 'transparent',
      },
    },
    {
      props: { variant: 'attention' },
      style: {
        [COLOR_VAR]: theme.palette.common.dijon1,
        backgroundColor: theme.palette.common.orange5,
      },
    },
    {
      props: { expanded: true },
      style: {
        '& .expandIcon': { transform: 'rotate(180deg)' },
        [CHEVRON_SELECTOR]: { color: `var(${COLOR_VAR})` },
      },
    },
  ],
}));

const HeaderTitle = styled(Typography)({
  textTransform: 'uppercase',
  color: `var(${COLOR_VAR})`,
});

export const ListAccordionHeader: FC<ListAccordionHeaderProps> = ({
  title,
  variant = 'default',
  counter,
}) => {
  const { expanded, toggle } = useContext(ListAccordionContext);

  return (
    <HeaderRoot variant={variant} expanded={expanded} onClick={toggle}>
      <Stack direction="row" alignItems="center" gap={0} py={1.25} flex={1}>
        <HeaderTitle variant="h3">{title}</HeaderTitle>
        {counter}
      </Stack>
      <IconButton inverse={!expanded}>
        <DropdownIcon className="expandIcon" />
      </IconButton>
    </HeaderRoot>
  );
};
